import { Syringe, AlertTriangle, MapPin, CheckCircle2, Package, CalendarDays, ClipboardList, TrendingUp, ShoppingCart, Flame } from 'lucide-react';
import { Link } from 'react-router-dom';

const Card = ({ title, icon: Icon, children, testId, className = '' }) => (
  <div className={`bg-[#0A0A0A] border border-[#27272A] rounded-sm p-5 ${className}`} data-testid={testId}>
    <h3 className="font-['Oswald'] text-sm uppercase tracking-[0.15em] text-[#D4AF37] flex items-center gap-2 mb-4">
      <Icon size={15} /> {title}
    </h3>
    {children}
  </div>
);

const Ring = ({ done, total }) => {
  const R = 34, C = 2 * Math.PI * R;
  const pct = total > 0 ? done / total : 0;
  return (
    <div className="relative w-24 h-24 flex-shrink-0" data-testid="today-ring">
      <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
        <circle cx="40" cy="40" r={R} fill="none" stroke="#1a1a1a" strokeWidth="7" />
        <circle cx="40" cy="40" r={R} fill="none" stroke="#C41E3A" strokeWidth="7" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - pct)} style={{ transition: 'stroke-dashoffset 0.7s ease' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-white font-bold text-lg leading-none">{done}/{total}</span>
        <span className="text-gray-500 text-[9px] uppercase tracking-wider mt-1">Done</span>
      </div>
    </div>
  );
};

const TILES = [
  { label: 'Injection Log', sub: 'Log & View', icon: Syringe, tab: 'injections' },
  { label: 'Calendar', sub: 'View Schedule', icon: CalendarDays, tab: 'calendar' },
  { label: 'Inventory', sub: 'Manage Vials', icon: Package, tab: 'inventory' },
  { label: 'Shop', sub: 'Browse Products', icon: ShoppingCart, to: '/#products' },
  { label: 'Progress', sub: 'Track Results', icon: TrendingUp, tab: 'progress' },
  { label: 'Protocols', sub: 'Your Protocols', icon: ClipboardList, tab: 'protocols' },
];

const tileCls = "bg-[#0A0A0A] border border-[#27272A] hover:border-[#C41E3A] rounded-sm p-4 flex flex-col items-center justify-center gap-2 text-center transition-colors group cursor-pointer";

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good Morning' : h < 18 ? 'Good Afternoon' : 'Good Evening';
};

export const DashboardTab = ({ data, vials = [], user, onLogNow, onNavigate }) => {
  if (!data) return null;
  const lowVials = vials.filter(v => v.active && v.low);
  const doneCount = data.today.filter(t => t.done).length;
  const name = data.first_name || user?.email?.split('@')[0] || 'Member';
  const week = data.week_stats || {};

  return (
    <div className="space-y-4" data-testid="member-dashboard">
      <div className="flex items-start justify-between gap-3" data-testid="dash-greeting">
        <div>
          <h2 className="font-['Oswald'] text-xl md:text-2xl uppercase tracking-wide text-white">{greeting()}, <span className="text-[#C41E3A]">{name}</span></h2>
          <p className="text-gray-500 text-xs mt-0.5 uppercase tracking-wider">Stay consistent. Stay disciplined.</p>
        </div>
        {data.streak > 0 && (
          <div className="flex items-center gap-1.5 bg-[#0A0A0A] border border-[#D4AF37]/50 rounded-sm px-3 py-2 flex-shrink-0" data-testid="streak-badge">
            <Flame size={16} className="text-[#C41E3A]" />
            <div className="leading-none">
              <p className="text-white font-bold text-sm">{data.streak}</p>
              <p className="text-[#D4AF37] text-[9px] uppercase tracking-wider mt-0.5">day streak</p>
            </div>
          </div>
        )}
      </div>

      {lowVials.length > 0 && (
        <div className="bg-[#C41E3A]/10 border border-[#C41E3A] rounded-sm p-4" data-testid="dash-low-inventory">
          <p className="flex items-center gap-2 text-[#C41E3A] text-xs font-bold uppercase tracking-wider mb-2"><Package size={14} /> Running Low — Time to Reorder</p>
          {lowVials.map(v => (
            <div key={v.vial_id} className="flex items-center justify-between py-1.5">
              <span className="text-white text-sm">{v.peptide_name} <span className="text-gray-400">— {v.remaining_value}{v.remaining_unit} left{v.days_remaining !== null ? ` (~${v.days_remaining} days)` : ''}</span></span>
              <Link to={v.product_id ? `/product/${v.product_id}` : '/#products'} className="bg-[#C41E3A] hover:bg-[#A01830] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm transition-colors" data-testid={`dash-reorder-${v.vial_id}`}>
                Reorder
              </Link>
            </div>
          ))}
        </div>
      )}

      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-5" data-testid="dash-today">
        <h3 className="font-['Oswald'] text-sm uppercase tracking-[0.15em] text-[#D4AF37] flex items-center gap-2 mb-4"><Syringe size={15} /> Today's Injections</h3>
        <div className="flex items-center gap-4">
          <div className="flex-1 min-w-0">
            {data.today.length === 0 ? (
              <p className="text-gray-500 text-sm">Nothing scheduled today.</p>
            ) : data.today.map(p => (
              <div key={p.protocol_id} className="flex items-center justify-between py-2 border-b border-[#1a1a1a] last:border-0">
                <div className="min-w-0 pr-2">
                  <p className="text-white text-sm font-semibold truncate">{p.peptide_name} <span className="text-gray-400 font-normal">— {p.dose}</span></p>
                  {p.time_of_day && <p className="text-gray-500 text-xs">{p.time_of_day}</p>}
                </div>
                {p.done ? (
                  <span className="flex items-center gap-1 text-green-400 text-xs font-bold uppercase flex-shrink-0" data-testid={`today-done-${p.protocol_id}`}><CheckCircle2 size={13} /> Done</span>
                ) : (
                  <button onClick={() => onLogNow(p)} className="bg-[#C41E3A] hover:bg-[#A01830] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm transition-colors flex-shrink-0" data-testid={`log-now-${p.protocol_id}`}>
                    Log Now
                  </button>
                )}
              </div>
            ))}
            <button onClick={() => onNavigate('calendar')} className="mt-3 border border-[#C41E3A]/60 hover:bg-[#C41E3A] text-[#C41E3A] hover:text-white text-[11px] font-bold uppercase tracking-wider px-4 py-1.5 rounded-sm transition-colors" data-testid="view-schedule-btn">
              View Calendar
            </button>
          </div>
          {data.today.length > 0 && <Ring done={doneCount} total={data.today.length} />}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="dash-tiles">
        {TILES.map(t => t.to ? (
          <Link key={t.label} to={t.to} className={tileCls} data-testid={`dash-tile-${t.label.toLowerCase().replace(/ /g, '-')}`}>
            <t.icon size={24} className="text-[#C41E3A] group-hover:scale-110 transition-transform" />
            <div>
              <p className="text-white text-xs font-bold uppercase tracking-wide">{t.label}</p>
              <p className="text-gray-500 text-[10px] mt-0.5">{t.sub}</p>
            </div>
          </Link>
        ) : (
          <button key={t.label} onClick={() => onNavigate(t.tab)} className={tileCls} data-testid={`dash-tile-${t.label.toLowerCase().replace(/ /g, '-')}`}>
            <t.icon size={24} className="text-[#C41E3A] group-hover:scale-110 transition-transform" />
            <div>
              <p className="text-white text-xs font-bold uppercase tracking-wide">{t.label}</p>
              <p className="text-gray-500 text-[10px] mt-0.5">{t.sub}</p>
            </div>
          </button>
        ))}
      </div>

      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-5" data-testid="dash-week-overview">
        <h3 className="font-['Oswald'] text-sm uppercase tracking-[0.15em] text-[#C41E3A] mb-3">This Week Overview</h3>
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-gray-500 text-xs uppercase tracking-wider">Total Injections</p>
            <p className="text-white text-2xl font-bold mt-0.5" data-testid="week-total-injections">{week.total_injections ?? 0}</p>
          </div>
          <div className="text-right">
            <p className="text-gray-500 text-xs uppercase tracking-wider">Completion Rate</p>
            <p className="text-white text-2xl font-bold mt-0.5" data-testid="week-completion-rate">{week.completion_rate !== null && week.completion_rate !== undefined ? `${week.completion_rate}%` : '—'}</p>
          </div>
        </div>
        <div className="h-1.5 bg-[#1a1a1a] rounded-full mt-3 overflow-hidden">
          <div className="h-full bg-[#C41E3A] rounded-full transition-all duration-700" style={{ width: `${week.completion_rate || 0}%` }} />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Site Rotation" icon={MapPin} testId="dash-rotation">
          {data.site_rotation.last_site ? (
            <p className="text-gray-300 text-sm">Last injection: <span className="text-white font-semibold">{data.site_rotation.last_site}</span></p>
          ) : (
            <p className="text-gray-500 text-sm">No injections logged yet.</p>
          )}
          <p className="text-gray-300 text-sm mt-2">Suggested next site: <span className="text-[#D4AF37] font-semibold" data-testid="site-suggestion">{data.site_rotation.suggestion}</span></p>
        </Card>

        <Card title="Missed (last 7 days)" icon={AlertTriangle} testId="dash-missed">
          {data.missed.length === 0 ? (
            <p className="text-green-400 text-sm flex items-center gap-1.5"><CheckCircle2 size={14} /> No missed doses. Nice work!</p>
          ) : data.missed.map((m, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 text-sm">
              <span className="text-white">{m.peptide_name} <span className="text-gray-500">— {m.dose}</span></span>
              <span className="text-[#C41E3A] text-xs">{m.day} {m.date.slice(5)}</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
};
